import { DisplayController, type FrameLimit } from '../core/display';

export class DisplayOptions {
  private readonly dialog = document.createElement('dialog');
  constructor(root: HTMLElement, private readonly display: DisplayController) {
    this.dialog.className = 'display-options';
    this.dialog.setAttribute('aria-labelledby', 'display-options-title');
    this.dialog.innerHTML = `
      <header class="options-heading">
        <div><span class="options-eyebrow">SETTINGS</span><h1 id="display-options-title">옵션</h1></div>
        <button type="button" class="options-close" aria-label="옵션 닫기">×</button>
      </header>
      <section class="display-panel" aria-labelledby="display-panel-title">
        <h2 id="display-panel-title">화면</h2>
        <div class="sync-row">
          <div class="sync-copy">
            <div class="sync-help" tabindex="0" aria-describedby="vsync-help">
              <span>수직 동기화</span><span class="help-dot" aria-hidden="true">i</span>
              <div id="vsync-help" class="options-tooltip" role="tooltip">사용자 모니터의 주사율에 맞춰 프레임을 자동으로 조절합니다. 켜져 있는 동안에는 프레임 설정이 자동으로 적용됩니다.</div>
            </div>
            <p>모니터에 맞춰 자동 조절</p>
          </div>
          <button type="button" class="sync-switch" role="switch" aria-label="수직 동기화" aria-describedby="vsync-help" aria-checked="true"><span data-sync-label>ON</span><i aria-hidden="true"></i></button>
        </div>
        <fieldset class="fps-settings" aria-describedby="fps-status">
          <legend>프레임 설정</legend>
          <div class="fps-choices">
            ${([[30, '최저'], [60, '보통'], [120, '최고']] as const).map(([fps, label]) => `<button type="button" class="fps-choice" data-fps="${fps}" aria-pressed="false"><span>${label}</span><strong>${fps}<small> FPS</small></strong></button>`).join('')}
          </div>
        </fieldset>
        <p id="fps-status" class="fps-status" aria-live="polite"></p>
      </section>
      <footer class="options-footer"><span>변경 사항은 자동 저장됩니다</span><button type="button" class="btn" data-done>완료</button></footer>`;
    root.append(this.dialog);
    const close = (): void => this.dialog.close();
    this.dialog.querySelector('.options-close')!.addEventListener('click', close);
    this.dialog.querySelector('[data-done]')!.addEventListener('click', close);
    this.dialog.querySelector('.sync-switch')!.addEventListener('click', () => {
      this.display.setVsync(!this.display.value.vsync);
      this.refresh();
    });
    this.dialog.querySelectorAll<HTMLButtonElement>('[data-fps]').forEach(button => {
      button.addEventListener('click', () => {
        this.display.setFps(Number(button.dataset.fps) as FrameLimit);
        this.refresh();
      });
    });
    // Modal shortcuts must not resume the game or select a character. Native Escape closes it.
    this.dialog.addEventListener('keydown', event => event.stopPropagation());
    this.dialog.addEventListener('keyup', event => event.stopPropagation());
  }
  open(): void {
    this.refresh();
    if (!this.dialog.open) this.dialog.showModal();
  }
  private refresh(): void {
    const { vsync, fps } = this.display.value;
    this.dialog.querySelector('.sync-switch')!.setAttribute('aria-checked', String(vsync));
    this.dialog.querySelector('[data-sync-label]')!.textContent = vsync ? 'ON' : 'OFF';
    this.dialog.querySelector<HTMLFieldSetElement>('.fps-settings')!.disabled = vsync;
    this.dialog.querySelectorAll<HTMLButtonElement>('[data-fps]').forEach(button => {
      button.setAttribute('aria-pressed', String(Number(button.dataset.fps) === fps));
    });
    this.dialog.querySelector('#fps-status')!.textContent = vsync
      ? '자동 조절 중 · 수직 동기화를 끄면 직접 선택할 수 있습니다.'
      : `최대 ${fps} FPS · 기기 성능과 화면 주사율에 따라 달라집니다.`;
  }
}
